import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Applications() {
  const [q, setQ] = useState("");
  const [ts, setTs] = useState("all");
  const [st, setSt] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [data, setData] = useState({ items: [], total: 0 });

  const load = async () => {
    const params = { page, page_size: pageSize };
    if (q.trim()) params.q = q.trim();
    if (ts !== "all") params.training_status = ts;
    if (st !== "all") params.status = st;
    const r = await api.get("/admin/applications", { params });
    setData(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, ts, st]);

  const onSearch = (e) => { e.preventDefault(); setPage(1); load(); };
  const totalPages = Math.max(1, Math.ceil(data.total / pageSize));

  const badge = (s) => {
    const c = {
      pending: "bg-amber-100 text-amber-700",
      under_review: "bg-indigo-100 text-indigo-700",
      approved: "bg-emerald-100 text-emerald-700",
      rejected: "bg-red-100 text-red-700",
    }[s] || "bg-zinc-100 text-zinc-700";
    return <span className={`text-xs font-bold uppercase tracking-wider rounded px-2 py-0.5 ${c}`}>{s}</span>;
  };

  return (
    <div className="p-8 lg:p-10">
      <div className="mb-6">
        <p className="tech-label">Records</p>
        <h1 className="font-display text-4xl font-black tracking-tighter">Applications.</h1>
      </div>

      <form onSubmit={onSearch} className="mb-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[240px]">
          <Input placeholder="Search by app no, name, mobile, email" value={q} onChange={(e) => setQ(e.target.value)} className="h-11 bg-white" data-testid="apps-search-input"/>
        </div>
        <Select value={ts} onValueChange={(v) => { setTs(v); setPage(1); }}>
          <SelectTrigger className="w-48 h-11" data-testid="apps-filter-training"><SelectValue placeholder="Training"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Training</SelectItem>
            <SelectItem value="with_training">With Training</SelectItem>
            <SelectItem value="without_training">Without Training</SelectItem>
          </SelectContent>
        </Select>
        <Select value={st} onValueChange={(v) => { setSt(v); setPage(1); }}>
          <SelectTrigger className="w-48 h-11" data-testid="apps-filter-status"><SelectValue placeholder="Status"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="under_review">Under Review</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <Button type="submit" className="h-11 bg-zinc-900 text-white" data-testid="apps-search-btn"><Search size={14} className="mr-2"/>Search</Button>
      </form>

      <div className="border border-zinc-200 rounded-lg bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-left">
            <tr>
              <th className="p-3 text-xs uppercase tracking-wider font-semibold text-zinc-600">App No</th>
              <th className="p-3 text-xs uppercase tracking-wider font-semibold text-zinc-600">Name</th>
              <th className="p-3 text-xs uppercase tracking-wider font-semibold text-zinc-600">Mobile</th>
              <th className="p-3 text-xs uppercase tracking-wider font-semibold text-zinc-600">Email</th>
              <th className="p-3 text-xs uppercase tracking-wider font-semibold text-zinc-600">Training</th>
              <th className="p-3 text-xs uppercase tracking-wider font-semibold text-zinc-600">Fee</th>
              <th className="p-3 text-xs uppercase tracking-wider font-semibold text-zinc-600">Status</th>
              <th className="p-3 text-xs uppercase tracking-wider font-semibold text-zinc-600 text-right">Action</th>
            </tr>
          </thead>
          <tbody data-testid="apps-table-body">
            {data.items.length === 0 ? (
              <tr><td colSpan={8} className="p-8 text-center text-zinc-400">No applications match your filters</td></tr>
            ) : data.items.map((r) => (
              <tr key={r.application_no} className="border-t border-zinc-100 hover:bg-zinc-50 transition-colors">
                <td className="p-3 font-mono text-xs">{r.application_no}</td>
                <td className="p-3">{r.full_name}</td>
                <td className="p-3">{r.mobile}</td>
                <td className="p-3 text-xs">{r.email}</td>
                <td className="p-3 text-xs">{r.training_status === "with_training" ? "With" : "Without"}</td>
                <td className="p-3">₹{r.fee_amount?.toLocaleString("en-IN")}</td>
                <td className="p-3">{badge(r.status)}</td>
                <td className="p-3 text-right">
                  <Link to={`/admin/applications/${r.id}`} className="text-xs font-semibold hover:underline" data-testid={`view-app-${r.application_no}`}>View →</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="p-3 border-t border-zinc-100 flex items-center justify-between text-xs">
          <p className="text-zinc-500">Total: <span className="font-semibold text-zinc-900">{data.total}</span></p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="apps-prev">Prev</Button>
            <span className="text-zinc-500">Page {page} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="apps-next">Next</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
